import * as React from "react";
import { useQuery } from "@tanstack/react-query";

import { shouldRetryRequest } from "api/apiError";
import { fetchRoles, roleKeys } from "api/rbacRoles";
import { fetchLoginHistory } from "api/settings";
import { fetchUsers } from "api/users";
import { useAuth } from "hooks/useAuth";
import { CLIENT_RECYCLE_EVENT, getClientRecycleBinEntries } from "utils/recycleBin";
import type { StoredAccount } from "./types";

const readStoredAccount = (): StoredAccount | null => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem("user") || "null");
    return parsed && typeof parsed === "object" ? (parsed as StoredAccount) : null;
  } catch {
    return null;
  }
};

const countRecycleBin = (): number => getClientRecycleBinEntries().length;

/** Counts and status shown on the Settings hub cards. */
export const useSettingsData = () => {
  const { user, hasPermission } = useAuth();
  const canSeeUsers = hasPermission("userManagement", "read");
  const canSeeRoles = hasPermission("roleManagement", "read");

  const storedAccount = React.useMemo(readStoredAccount, []);
  const userId = user?._id || storedAccount?._id || storedAccount?.id || "";
  const fallbackUserName =
    user?.name ||
    user?.email ||
    storedAccount?.name ||
    storedAccount?.userName ||
    storedAccount?.email ||
    "";

  // Same keys as pages/Roles, so the hub and the roles page share one cache.
  const users = useQuery({
    queryKey: ["rbac", "users"],
    queryFn: fetchUsers,
    retry: shouldRetryRequest,
    enabled: canSeeUsers,
  });
  const roles = useQuery({
    queryKey: roleKeys.all,
    queryFn: fetchRoles,
    retry: shouldRetryRequest,
    enabled: canSeeRoles,
  });
  const logins = useQuery({
    queryKey: ["rbac", "logins", userId],
    queryFn: () => fetchLoginHistory(userId),
    retry: shouldRetryRequest,
    enabled: Boolean(userId),
  });

  // The recycle bin lives in localStorage; RecycleBinView announces every change with an event.
  const [recycleCount, setRecycleCount] = React.useState<number>(countRecycleBin);
  React.useEffect(() => {
    const refresh = () => setRecycleCount(countRecycleBin());
    window.addEventListener(CLIENT_RECYCLE_EVENT, refresh);
    return () => {
      window.removeEventListener(CLIENT_RECYCLE_EVENT, refresh);
    };
  }, []);

  const userList = users.data || [];

  return {
    canSeeUsers,
    canSeeRoles,
    userId,
    fallbackUserName,
    userCount: users.isSuccess ? userList.length : null,
    activeUserCount: users.isSuccess ? userList.filter((entry) => entry.isActive).length : null,
    roleCount: roles.isSuccess ? (roles.data || []).length : null,
    loginEntries: logins.data || [],
    loginsLoading: logins.isLoading && logins.fetchStatus !== "idle",
    lastSignIn: logins.data?.[0]?.timestamp || null,
    recycleCount,
  };
};
